import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Code } from "@phosphor-icons/react";
import api from "@/lib/api";

/**
 * Compact super-admin-only widget for the Dashboard. Fetches the top-3
 * complexity offenders per side from /api/_admin/complexity and renders them
 * in the Swiss-brutalist card style. Deep-links to /admin/code-health.
 *
 * The full endpoint runs `radon` + a JS AST walker; a cached call typically
 * returns in ~200ms. If it fails or the user isn't a super_admin (server
 * returns 403), the widget renders nothing — no console noise.
 */
function rankPill(rank) {
  switch (rank) {
    case "A":
      return "bg-emerald-500 border-emerald-700 text-black";
    case "B":
      return "bg-lime-400 border-lime-700 text-black";
    case "C":
      return "bg-amber-300 border-amber-700 text-black";
    case "D":
      return "bg-orange-400 border-orange-700 text-white";
    case "E":
      return "bg-rose-500 border-rose-800 text-white";
    case "F":
      return "bg-black border-black text-white";
    default:
      return "bg-white border-zinc-400 text-black";
  }
}

function SideMini({ label, side, testIdPrefix }) {
  const rows = side?.top_offenders?.slice(0, 3) || [];
  return (
    <div className="flex-1 min-w-0" data-testid={`${testIdPrefix}-mini`}>
      <div className="flex items-baseline justify-between border-b border-black pb-2 mb-2">
        <div className="label-mono">{label}</div>
        <div className="text-[11px] text-zinc-600 font-mono">
          {side?.total_functions ?? 0} fns · max{" "}
          <span className="font-bold text-black">{side?.summary?.max ?? 0}</span>
        </div>
      </div>
      <ul className="space-y-1">
        {rows.map((r, i) => (
          <li key={`${r.file}-${r.line}-${r.name}`} className="flex items-center justify-between gap-2 text-[12px]" data-testid={`${testIdPrefix}-mini-row-${i}`}>
            <div className="min-w-0 flex-1">
              <div className="font-mono truncate" title={r.name}>{r.name}</div>
              <div className="font-mono text-[10px] text-zinc-500 truncate" title={`${r.file}:${r.line}`}>
                {r.file}
                <span className="text-zinc-400">:{r.line}</span>
              </div>
            </div>
            <span className="font-bold tabular-nums">{r.complexity}</span>
            <span className={`border px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-widest ${rankPill(r.rank)}`}>
              {r.rank}
            </span>
          </li>
        ))}
        {!rows.length && (
          <li className="label-mono text-zinc-500">No data.</li>
        )}
      </ul>
    </div>
  );
}

export default function CodeHealthMini() {
  const [data, setData] = useState(null);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    api
      .get("/_admin/complexity", { params: { top: 3 } })
      .then((r) => setData(r.data))
      .catch((e) => {
        // 403 (non-super-admin) or 401 (session lost) — silently hide.
        if (e.response?.status === 403 || e.response?.status === 401) setHidden(true);
        else setHidden(true); // network / 500 — also just hide, don't clutter dashboard
      });
  }, []);

  if (hidden) return null;

  return (
    <div className="brut-card p-5" data-testid="dashboard-code-health-mini">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Code size={18} weight="bold" />
          <div className="font-chivo font-bold uppercase tracking-tight">Code health</div>
          <span className="label-mono">super_admin only</span>
        </div>
        <Link
          to="/admin/code-health"
          data-testid="dashboard-code-health-mini-link"
          className="text-[11px] uppercase tracking-widest border border-black px-2 py-1 hover:bg-black hover:text-white transition-colors duration-100"
        >
          Full report <ArrowUpRight className="inline" size={12} />
        </Link>
      </div>
      {!data ? (
        <div className="label-mono text-zinc-500">Loading…</div>
      ) : (
        <div className="flex flex-col md:flex-row gap-6">
          <SideMini label="Python" side={data.python} testIdPrefix="dashboard-code-health-py" />
          <SideMini label="JavaScript" side={data.javascript} testIdPrefix="dashboard-code-health-js" />
        </div>
      )}
    </div>
  );
}
